<template>
  <Transition name="push-prompt">
    <div
      v-if="visible"
      class="push-prompt"
      role="dialog"
      aria-labelledby="push-prompt-title"
    >
      <button type="button" class="push-prompt-close" aria-label="Zatvori" @click="close">
        <i class="esei icon-close" aria-hidden="true"></i>
      </button>

      <div class="push-prompt-header">
        <span class="push-prompt-icon"><i class="esei icon-bell-solid" aria-hidden="true"></i></span>
        <strong id="push-prompt-title">
          {{ details ? 'Izaberite obaveštenja' : 'Obaveštenja sa Pijace.com' }}
        </strong>
      </div>

      <!-- osnovni prikaz -->
      <template v-if="!details">
        <p class="push-prompt-text">Obavestićemo vas o novim vestima, događajima, receptima i oglasima.</p>

        <div class="push-prompt-actions">
          <button type="button" class="btn btn-light btn-sm" :disabled="busy" @click="details = true">
            Detaljnije
          </button>
          <button type="button" class="btn btn-sm push-prompt-primary" :disabled="busy" @click="accept(availableCategories)">
            U redu
          </button>
        </div>
      </template>

      <!-- izbor kategorija -->
      <template v-else>
        <div class="push-prompt-options">
          <label v-for="item in options" :key="item.value" class="push-prompt-option">
            <span>{{ item.label }}</span>
            <span class="form-check form-switch m-0">
              <input v-model="selected" class="form-check-input" type="checkbox" role="switch" :value="item.value">
            </span>
          </label>
        </div>

        <!-- nema dugmeta "Ne želim" - odbijanje je samo preko X -->
        <div class="push-prompt-actions">
          <button type="button" class="btn btn-sm push-prompt-primary" :disabled="busy || !selected.length" @click="accept(selected)">
            Sačuvaj
          </button>
        </div>
      </template>

      <p v-if="error" class="push-prompt-error">{{ error }}</p>
    </div>
  </Transition>
</template>

<script setup>
const SHOW_DELAY_MS = 10000
const CLOSE_DAYS = 7      // X - ponovo pitamo posle nedelju dana
const STORAGE_KEY = 'push_prompt_hidden_until'

const push = usePushNotifications()

const visible = ref(false)

// dok je kartica otvorena, prozor "Budi deo Pijace.com" ceka (components/push/JoinPrompt.vue)
const pushOpen = useState('push_prompt_open', () => false)
const joinOpen = useState('join_prompt_open', () => false)
watch(visible, (open) => { pushOpen.value = open })
const details = ref(false)
const busy = ref(false)
const error = ref('')
const selected = ref([])

// iste kategorije za sve, ulogovane i goste (lista je u composable-u)
const options = PUSH_CATEGORY_OPTIONS

const availableCategories = PUSH_ALL_CATEGORIES

// po defaultu je sve uključeno i u detaljnom prikazu
watch(details, (open) => { if (open) selected.value = [...availableCategories] })

let timer = null
let unmounted = false

onMounted(async () => {
  if (isHiddenByUser()) return

  // prikazuje se ako browser podržava push i korisnik još nije odgovorio browseru ('default'),
  // ili je dozvola već data, a uređaj nema (ispravnu) pretplatu - inače takav uređaj
  // (npr. telefon sa dozvoljenim obaveštenjima) nikad ne bi dobio ponudu da se prijavi
  const permission = push.currentPermission()
  const show = permission === 'default' || (permission === 'granted' && await push.needsSubscription())

  if (!show || unmounted) return

  timer = setTimeout(showWhenFree, SHOW_DELAY_MS)
})

// prozor "Kreiraj nalog" (JoinPrompt) izlazi ranije i dok je otvoren, ova kartica ceka (proverava na 3 s)
function showWhenFree() {
  if (unmounted) return

  if (joinOpen.value) {
    timer = setTimeout(showWhenFree, 3000)
    return
  }

  visible.value = true
}

onBeforeUnmount(() => {
  unmounted = true
  clearTimeout(timer)
  pushOpen.value = false
})

async function accept(categories) {
  busy.value = true
  error.value = ''

  try {
    const result = await push.subscribe(categories)

    // korisnik je odbio u browseru - kartica se više ne prikazuje (permission više nije 'default')
    if (!result && push.currentPermission() === 'denied') {
      visible.value = false
      return
    }

    // zatvorio je browserov prozor bez odgovora - pitamo ponovo kasnije
    if (!result) {
      hideFor(CLOSE_DAYS)
      return
    }

    visible.value = false
  } catch (e) {
    error.value = 'Uključivanje nije uspelo. Pokušajte ponovo kasnije.'
  } finally {
    busy.value = false
  }
}

const close = () => hideFor(CLOSE_DAYS)

function hideFor(days) {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000))
  } catch (e) {
    // privatni prozor ili blokiran storage - samo zatvaramo karticu
  }
  visible.value = false
}

function isHiddenByUser() {
  try {
    return Number(localStorage.getItem(STORAGE_KEY) || 0) > Date.now()
  } catch (e) {
    return false
  }
}
</script>
